import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdDelete, MdCallMerge, MdAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import { Autocomplete, TextField } from '@mui/material';

import { BaseURL } from '../../config/apiUrl';
import { Get, Post, Patch } from '../../Axios/AxiosFunctions';
import { Button } from '../../Component/Button/Button';
import Loader from '../../Component/Loader';
import ModalSkeleton from '../../modals/ModalSkeleton';
import { DropDown } from '../../Component/DropDown/DropDown';
import classes from './ExtractionReview.module.css';

const money = (n) =>
  n == null ? '' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Expense-row confirmation.
 *
 * The row set is the single decision that determines the whole workbook, totals, EBITDA,
 * net income, the add-back block and every cross-tab formula derive from it. So the analyst
 * confirms this list once, rather than reviewing hundreds of individual lines.
 *
 * Each row keeps its `sources` (the accounts feeding it) separately from its label, so a
 * rename does not break the link to the data.
 */
const ConfirmRowsModal = ({ show, setShow, runId, onConfirmed }) => {
  const { access_token: accessToken } = useSelector((state) => state.authReducer);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dropped, setDropped] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [advisories, setAdvisories] = useState([]);
  const [factorInput, setFactorInput] = useState('1');
  const [savingFactor, setSavingFactor] = useState(false);
  const proForma = columns.find((c) => c.tag === 'Pro Forma');

  const saveFactor = async () => {
    const f = Number(factorInput);
    if (!Number.isFinite(f) || f <= 0 || f > 5) {
      toast.error('Enter a positive number up to 5, for example 1.05');
      return;
    }
    setSavingFactor(true);
    const res = await Post(
      BaseURL(`extraction/run/${runId}/pro-forma-factor`),
      { factor: f },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    setSavingFactor(false);
    if (res?.data?.data) {
      toast.success(`Projection adjusted to ${f}x`);
      load();
    } else {
      toast.error('Could not apply that adjustment');
    }
  };
  const [mergeFrom, setMergeFrom] = useState(null);
  const [acked, setAcked] = useState({});
  // Unanswered lines are deduplicated by label across documents, so the label is their key.
  // A withheld figure belongs to one line of one document: "Compensation of officers" can
  // be misread on the 2023 return and the 2024 one, and each needs its own answer.
  const dkey = (d) =>
    d.withheld
      ? `w|${d.docIndex}|${d.lineIndex}|${String(d.label).toLowerCase()}`
      : `d|${String(d.label).toLowerCase()}`;
  const [accounts, setAccounts] = useState([]);
  // DropDown reads option[optionLabel]; a bare string array renders as blank rows.
  const accountOptions = accounts.map((a) => ({ label: a, value: a }));
  const [resolving, setResolving] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await Get(BaseURL(`extraction/run/${runId}/expense-rows`), accessToken);
    const d = res?.data?.data;
    if (d) {
      const confirmed = d.confirmed?.rows?.length ? d.confirmed.rows : null;
      setRows(
        (confirmed || d.rows).map((r) => ({
          label: r.label,
          sources: r.sources || [r.label],
          amounts: d.rows.find((x) => x.label === r.label)?.amounts || [],
          composedFrom: d.rows.find((x) => x.label === r.label)?.composedFrom,
        })),
      );
      setUnmatched(d.unmatched || []);
      setCoverage(d.coverage || []);
      setAdvisories(d.advisories || []);
      setColumns(d.columns || []);
      setDropped(d.dropped || []);
    }
    const acc = await Get(BaseURL('extraction/accounts'), accessToken);
    setAccounts(
      (acc?.data?.data?.detail || [])
        .filter((a) => a.section === 'expenses')
        .map((a) => a.account),
    );
    setLoading(false);
  };

  /**
   * Resolve a decision in place. The line has no account, so the fix is to give it one, * the same PATCH the review table makes. Doing it here means the analyst never has to
   * leave the layout screen to answer a question the layout screen asked.
   */
  // Correct a figure the parser misread (typically an IRS line number fused onto the amount:
  // "712,500" for a printed 12,500). The correction is recorded beside the parsed value and
  // listed on the workbook's DECISIONS tab; the line then reaches the sheet like any other.
  const [amountDraft, setAmountDraft] = useState({});
  const correctAmount = async (n) => {
    const raw = String(amountDraft[dkey(n)] ?? '').replace(/[$,\s]/g, '');
    const value = Number(raw.replace(/^\((.*)\)$/, '-$1'));
    if (raw === '' || !Number.isFinite(value)) {
      toast.error('Enter the amount as printed on the document');
      return;
    }
    setResolving(`${n.docIndex}:${n.lineIndex}`);
    const res = await Patch(
      BaseURL(`extraction/run/${runId}/document/${n.docIndex}/line/${n.lineIndex}/amount`),
      { amount: value, fromLayout: true },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    setResolving(null);
    if (res?.data?.data) {
      toast.success(`Corrected to ${money(value)}`);
      load();
    }
  };

  const assignAccount = async (n, account) => {
    if (!account) return;
    setResolving(`${n.docIndex}:${n.lineIndex}`);
    const res = await Patch(
      BaseURL(`extraction/run/${runId}/document/${n.docIndex}/line/${n.lineIndex}`),
      { account },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    setResolving(null);
    const data = res?.data?.data;
    // The endpoint answers "did you mean an existing account?" with a normal response, so
    // a truthy body is not proof it saved.
    if (data?.needsConfirmation) {
      const use = window.confirm(
        `"${data.proposed}" looks like "${data.suggestion}", which already exists ` +
          `(${data.why}).\n\nOK to use "${data.suggestion}", or Cancel to add yours to ` +
          `the chart.`,
      );
      setResolving(`${n.docIndex}:${n.lineIndex}`);
      const retry = await Patch(
        BaseURL(`extraction/run/${runId}/document/${n.docIndex}/line/${n.lineIndex}`),
        use ? { account: data.suggestion } : { account: data.proposed, force: true },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setResolving(null);
      if (retry?.data?.data) {
        toast.success(`${n.label} -> ${use ? data.suggestion : data.proposed}`);
        load();
      }
      return;
    }
    if (data) {
      toast.success(`${n.label} -> ${account}`);
      load();
    } else {
      toast.error('Could not set the account');
    }
  };
  useEffect(() => {
    // "Left it out" is keyed by the line itself, not its position: answering one decision
    // reloads the plan and removes it from the list, which used to shift every later choice
    // onto the wrong row -- and load() cleared them all anyway.
    setAcked({});
    if (runId) load();
  }, [runId]);

  const rename = (i, label) =>
    setRows((rs) => rs.map((r, x) => (x === i ? { ...r, label } : r)));
  const remove = (i) => setRows((rs) => rs.filter((_, x) => x !== i));

  // merging folds one row's sources into another, the same operation the analysts'
  // compositions perform (Rent = rent + property tax)
  const mergeInto = (target) => {
    if (mergeFrom == null || mergeFrom === target) return setMergeFrom(null);
    setRows((rs) => {
      const src = rs[mergeFrom];
      return rs
        .map((r, x) =>
          x === target
            ? {
                ...r,
                sources: [...new Set([...r.sources, ...src.sources])],
                composedFrom: [
                  ...(r.composedFrom || r.sources.map((s) => ({ account: s }))),
                  ...src.sources.map((s) => ({ account: s })),
                ],
              }
            : r,
        )
        .filter((_, x) => x !== mergeFrom);
    });
    setMergeFrom(null);
  };

  // Ash's sheets carry rows no source document names: "Travel Meals", "Cash Over Short",
  // "Reconciliation Discrepancies". addUnmatched can only promote an account the mapper
  // already found, so those were unreachable and the analyst had to leave the sheet to add
  // them. A row created here starts empty and is filled by merging real accounts into it,
  // which keeps every figure traceable to a document.
  const [newRow, setNewRow] = useState('');
  const addOwnRow = () => {
    const label = newRow.trim();
    if (!label) return;
    if (rows.some((r) => r.label.toLowerCase() === label.toLowerCase())) {
      toast.error('That row is already on the sheet');
      return;
    }
    setRows((rs) => [...rs, { label, sources: [], amounts: [], addedByAnalyst: true }]);
    setNewRow('');
  };

  const addUnmatched = (u) => {
    setRows((rs) => [...rs, { label: u.account, sources: [u.account], amounts: [] }]);
    setUnmatched((us) => us.filter((x) => x !== u));
  };

  const save = async () => {
    setSaving(true);
    const res = await Post(
      BaseURL(`extraction/run/${runId}/expense-rows`),
      { rows: rows.map((r) => ({ label: r.label, sources: r.sources })) },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    setSaving(false);
    if (res?.data) {
      toast.success(`Confirmed ${rows.length} expense rows`);
      onConfirmed?.();
      setShow(false);
    } else {
      toast.error('Could not save the layout');
    }
  };

  return (
    <ModalSkeleton
      show={show}
      setShow={setShow}
      modalClass={classes.pickerModal}
      width={'1120px'}
      header={'Confirm Expense Rows'}
      showCloseIcon={true}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {(() => {
            // A line-level item here is the SAME question Review & approve already asked:
            // it carries docIndex/lineIndex and is answered by giving one printed line an
            // account. Peak Antler surfaced "UPS - FED EX" four times, once per document
            // per occurrence, as though each were a fresh decision. They are deduplicated
            // by label and labelled as carried over, so this step reads as what it is: the
            // sheet, not a second pass over the documents.
            const seen = new Set();
            const decisions = [
              ...coverage.flatMap((c) =>
                (c.withheld || []).map((w) => ({ ...w, column: c.column, withheld: true })),
              ),
              ...coverage.flatMap((c) =>
                (c.needsDecision || []).map((n) => ({ ...n, column: c.column })),
              ),
            ].filter((d) => {
              const key = dkey(d);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            const gaps = coverage.filter((c) => c.status === 'unaccounted');
            const open = decisions.filter((d) => !acked[dkey(d)]).length;
            return (
              <>
                <div className={classes.rowsIntro}>
                  <div className={classes.rowsHeadline}>
                    <b>{rows.length} expense rows</b> will be written to the workbook.
                    {open > 0 && (
                      <span className={classes.rowsHeadlineWarn}>
                        {' '}
                        {open} decision{open === 1 ? '' : 's'} to make first.
                      </span>
                    )}
                  </div>
                  <div className={classes.rowsSub}>
                    This layout drives every total on the sheet and all seven tabs. Rename a row
                    to change how it reads, merge two rows to combine them, or delete a row to
                    leave it out.
                  </div>
                </div>

                {decisions.length > 0 && (
                  <div className={classes.decisions}>
                    <div className={classes.decisionsTitle}>
                      Still unanswered from Review &amp; approve
                    </div>
                    <div className={classes.decisionsSub}>
                      These are lines in the statements that have no account yet, so their
                      money is not on the sheet. Answering one here is the same as answering
                      it back on Review &amp; approve.
                    </div>
                    {decisions.map((n, i) => (
                      <div
                        className={`${classes.decision} ${acked[dkey(n)] ? classes.decisionDone : ''}`}
                        key={i}
                      >
                        <div className={classes.decisionMain}>
                          <span className={classes.decisionLabel}>{n.label}</span>
                          <span className={classes.decisionAmt}>{money(n.value)}</span>
                          <span className={classes.decisionCol}>{n.column}</span>
                        </div>
                        <div className={classes.decisionWhy}>{n.reason}</div>
                        <div className={classes.decisionActions}>
                          {acked[dkey(n)] ? (
                            <span
                              className={classes.decisionUndo}
                              onClick={() => setAcked((a) => ({ ...a, [dkey(n)]: false }))}
                            >
                              left out, undo
                            </span>
                          ) : (
                            <>
                              {n.withheld && n.lineIndex != null && (
                                <div className={classes.correctAmount}>
                                  <input
                                    className={classes.correctAmountInput}
                                    inputMode="decimal"
                                    placeholder="Amount as printed"
                                    value={amountDraft[dkey(n)] ?? ''}
                                    onChange={(e) =>
                                      setAmountDraft((a) => ({ ...a, [dkey(n)]: e.target.value }))
                                    }
                                    onKeyDown={(e) => e.key === 'Enter' && correctAmount(n)}
                                    disabled={resolving != null}
                                  />
                                  <span
                                    className={classes.decisionBtnPrimary}
                                    onClick={() => resolving == null && correctAmount(n)}
                                  >
                                    {resolving === `${n.docIndex}:${n.lineIndex}`
                                      ? 'Saving…'
                                      : 'Correct amount'}
                                  </span>
                                </div>
                              )}
                              {!n.withheld && (
                              <div className={classes.decisionPick}>
                                <DropDown
                                  options={accountOptions}
                                  optionLabel="label"
                                  optionValue="value"
                                  value={null}
                                  setter={(v) => v?.value && assignAccount(n, v.value)}
                                  placeholder={
                                    resolving === `${n.docIndex}:${n.lineIndex}`
                                      ? 'Saving…'
                                      : 'Include as…'
                                  }
                                  disabled={resolving != null}
                                />
                              </div>
                              )}
                              <span
                                className={classes.decisionBtn}
                                onClick={() => setAcked((a) => ({ ...a, [dkey(n)]: true }))}
                              >
                                Leave it out
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {gaps.map((g) => (
                  <div className={classes.gapNote} key={g.column}>
                    <b>
                      {g.column}: {money(Math.abs(g.unaccounted))} of stated expense is not on
                      the sheet.
                    </b>
                    <div className={classes.gapWhy}>{g.explanation}</div>
                    <div className={classes.gapWhy}>
                      Generating now will understate costs for {g.column.split(' ')[0]} by that
                      amount, which flows through to EBITDA and SDE.
                    </div>
                  </div>
                ))}

                {/* Judgment calls the SHEET needs, not a single line's account. Placed
                    ABOVE the coverage panel because these change the numbers an analyst is
                    about to sign off on, and nothing else on the screen asks about them. */}
                <div className={classes.addOwn}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={accounts}
                    value={newRow}
                    onChange={(e, v) => setNewRow(v || '')}
                    onInputChange={(e, v) => setNewRow(v || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Add an expense row of your own"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOwnRow();
                          }
                        }}
                      />
                    )}
                  />
                  <Button label="Add row" disabled={!newRow.trim()} onClick={addOwnRow}>
                    <MdAdd />
                  </Button>
                </div>

                {advisories.length > 0 && (
                  <>
                    <div className={classes.covTitle}>
                      Decisions for this sheet ({advisories.length})
                    </div>
                    <div className={classes.advisories}>
                      {advisories.map((a, i) => (
                        <div
                          key={i}
                          className={`${classes.advisory} ${
                            a.severity === 'blocking' ? classes.advisoryBlock : ''
                          }`}
                        >
                          <div className={classes.advisoryHead}>
                            <span className={classes.advisoryTag}>
                              {a.severity === 'blocking' ? 'Must resolve' : 'Confirm'}
                            </span>
                            {a.column ? <span>{a.column}</span> : null}
                          </div>
                          <div className={classes.advisorySummary}>{a.summary}</div>
                          <div className={classes.advisoryQuestion}>{a.question}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className={classes.covTitle}>Where each column comes from</div>
                <div className={classes.coverage}>
                  {coverage.map((c) => {
                    const src = columns.find((x) => x.column === c.column);
                    const cls =
                      c.status === 'complete'
                        ? classes.covComplete
                        : c.status === 'needs_decision'
                        ? classes.covShort
                        : classes.covOver;
                    return (
                      <div className={classes.covCell} key={c.column}>
                        <div className={classes.covCol}>{c.column}</div>
                        <div className={classes.covFile} title={src?.fileName}>
                          {src?.fileName || ''}
                        </div>
                        <div className={classes.covNums}>
                          {c.placed.count} line{c.placed.count === 1 ? '' : 's'} ·{' '}
                          {money(c.placed.value)}
                        </div>
                        <div className={`${classes.covStatus} ${cls}`}>
                          {c.status === 'complete'
                            ? 'every line accounted for'
                            : c.status === 'needs_decision'
                            ? `${c.needsDecision.length} to decide`
                            : `${money(Math.abs(c.unaccounted))} unaccounted`}
                        </div>
                        <div className={classes.covExplain}>{c.explanation}</div>
                        {(c.withheld || []).map((w, i) => (
                          <div className={classes.covWithheld} key={i} title={w.reason}>
                            withheld: {w.label} {money(w.value)}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {dropped.length > 0 && (
                  <div className={classes.rowsDropped}>
                    Not used: {dropped.join(' · ')}
                  </div>
                )}
              </>
            );
          })()}

          {proForma && (
            <div className={classes.proForma}>
              <div>
                <strong>{proForma.column}</strong> projects the part-year to a full
                year ({proForma.fileName}). A seasonal business does not earn evenly,
                so adjust it if the remaining months will not match the ones so far.
              </div>
              <div className={classes.proFormaRow}>
                <label htmlFor="pf">Adjustment</label>
                <input
                  id="pf"
                  className={classes.proFormaInput}
                  value={factorInput}
                  onChange={(e) => setFactorInput(e.target.value)}
                  placeholder="1.00"
                />
                <span className={classes.proFormaHint}>
                  1.00 = as calculated, 1.05 = 5% higher, 0.90 = 10% lower
                </span>
                <Button
                  label={savingFactor ? 'Saving...' : 'Apply'}
                  disabled={savingFactor || !factorInput}
                  onClick={saveFactor}
                />
              </div>
            </div>
          )}

          <div className={classes.covTitle}>
            Expense rows ({rows.length}), one row per account, newest year first
          </div>
          {/* One cell per column, matched by name. The header used to join the years into
              a single span while the body rendered amounts positionally, so a row missing
              a year silently shifted every figure one column left and the numbers did not
              line up with the headings above them. */}
          <div className={classes.rowsHead}>
            <span>Row label</span>
            <div className={classes.rowsAmounts}>
              {columns.map((c) => (
                <span
                  key={c.column}
                  className={c.tag === 'Pro Forma' ? classes.colProForma : ''}
                  title={c.fileName}
                >
                  {c.tag === 'Pro Forma' ? `${c.year} proj.` : c.year}
                </span>
              ))}
            </div>
            <span>edit</span>
          </div>

          <div className={classes.rowsBody}>
            {rows.map((r, i) => (
              <div
                className={`${classes.rowsRow} ${mergeFrom === i ? classes.rowsMerging : ''}`}
                key={`${r.label}-${i}`}
              >
                <input
                  className={classes.rowsInput}
                  value={r.label}
                  onChange={(e) => rename(i, e.target.value)}
                />
                <div className={classes.rowsAmounts}>
                  {columns.map((c) => {
                    const a = r.amounts.find((x) => x.column === c.column);
                    return (
                      <span
                        key={c.column}
                        className={c.tag === 'Pro Forma' ? classes.colProForma : ''}
                      >
                        {a ? money(a.value) : ''}
                      </span>
                    );
                  })}
                </div>
                <div className={classes.rowsActions}>
                  {mergeFrom == null ? (
                    <MdCallMerge title="merge this row into another" onClick={() => setMergeFrom(i)} />
                  ) : (
                    <span className={classes.rowsMergeTarget} onClick={() => mergeInto(i)}>
                      {mergeFrom === i ? 'cancel' : 'merge here'}
                    </span>
                  )}
                  <MdDelete title="remove this row" onClick={() => remove(i)} />
                </div>
                <div className={classes.rowsComposed}>
                  {r.composedFrom?.length > 1 ? (
                    <>
                      combined from{' '}
                      <b>{r.composedFrom.map((c) => c.account).join(' + ')}</b>
                    </>
                  ) : r.amounts.length ? (
                    <>
                      from <b>{(r.sources || []).join(', ')}</b> · appears in{' '}
                      {r.amounts.length} of {columns.length} year
                      {columns.length === 1 ? '' : 's'}
                    </>
                  ) : (
                    'added manually, with no source figures'
                  )}
                </div>
              </div>
            ))}
          </div>

          {unmatched.length > 0 && (
            <div className={classes.rowsUnmatched}>
              <b>{unmatched.length} account(s) excluded.</b> Not recognised as operating
              expenses, so they are left out unless you add them. Anything genuinely an
              expense should be added, the coverage figures above will then balance:
              {unmatched.map((u, i) => (
                <span key={i} onClick={() => addUnmatched(u)}>
                  <MdAdd /> {u.account} ({money(u.value)})
                </span>
              ))}
            </div>
          )}

          <div className={classes.rowsFooter}>
            <span className={classes.rowsFooterNote}>
              Saved to this run, the workbook is built from exactly these rows.
            </span>
            <Button label={'Reset'} onClick={load} disabled={saving} />
            <Button
              label={saving ? 'Saving…' : `Confirm ${rows.length} rows`}
              onClick={save}
              disabled={saving || !rows.length}
            />
          </div>
        </>
      )}
    </ModalSkeleton>
  );
};

export default ConfirmRowsModal;
